import React, { useState } from 'react';
import { Box, LoadingSpinner } from 'edenred-ui';
import { useTranslation } from 'react-i18next';

import { CreateBenefitGroupForm } from '@epi-forms/BenefitGroup';
import {
  BenefitGroupName,
  DialogCreateAnotherGroup,
  LunchSettings,
  NotificationCreateAnotherGroup,
  SelectBenefitsBar,
  MyChoiceDisclaimer
} from '@epi-shared/components';
import { useGetDefaultBenefitGroupsSettingsQuery } from '@epi-api/benefitGroups';
import {
  IBenefitDefaultSettings,
  ICreateBenefitGroupForm,
  IBenefits
} from '@epi-models/pages/BenefitGroups';
import { mapBenefitsToSettings } from '@epi-helpers/BenefitGroupHelper';
import { LunchFundsLoadingValues } from '@epi-models/pages/Home';

import { AnnualLimitsGuideDot } from './components';
import { MyChoiceBenefitsList } from '@epi-shared/components/BenefitGroups/SettingsSections/MyChoiceBenefitsList/MyChoiceBenefitsList';

const createDefaultValuesFromSettings = (
  defaultSettings?: IBenefitDefaultSettings
): ICreateBenefitGroupForm => {
  const benefitSettings = mapBenefitsToSettings(defaultSettings) as Record<
    IBenefits,
    {
      maxAmountPerPeriod: number;
      isMaximumSpendingLimit: boolean;
      fundsLoadingType: LunchFundsLoadingValues;
      recurringTopupRequestEnabled: boolean;
    }
  >;

  return {
    groupName: '',
    benefitSettings
  };
};

export const CreateBenefitGroup = () => {
  const { t } = useTranslation();
  const [isOpenModal, setIsOpenModal] = useState(false);

  const { data: benefitsDefaultSettings, isLoading } =
    useGetDefaultBenefitGroupsSettingsQuery();

  const defaultFormSettings = createDefaultValuesFromSettings(
    benefitsDefaultSettings
  );

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!benefitsDefaultSettings?.contractedBenefits.length) {
    return <div>{t('containers.benefit_groups.no_result')}</div>;
  }

  return (
    <CreateBenefitGroupForm
      defaultSettings={defaultFormSettings}
      toggleModal={setIsOpenModal}
    >
      <NotificationCreateAnotherGroup />
      <Box display="flex" flexDirection="column" gap={3} position="relative">
        <BenefitGroupName />
        <SelectBenefitsBar
          contractedBenefits={benefitsDefaultSettings.contractedBenefits}
        />
        <LunchSettings defaultSettings={benefitsDefaultSettings} />
        <Box
          display="flex"
          flexDirection="row"
          flexWrap="wrap"
          gap={3}
          mt={1}
          position="relative"
        >
          <AnnualLimitsGuideDot />
          <MyChoiceBenefitsList
            contractedBenefits={benefitsDefaultSettings.contractedBenefits}
            companyMyChoiceEnabled={
              benefitsDefaultSettings.companyMyChoiceEnabled
            }
          />
        </Box>
        <MyChoiceDisclaimer benefitsDefaultSettings={benefitsDefaultSettings} />
      </Box>
      <DialogCreateAnotherGroup
        openModal={isOpenModal}
        toggleModal={setIsOpenModal}
        defaultSettings={defaultFormSettings}
      />
    </CreateBenefitGroupForm>
  );
};
